import {
  AccountPage,
  BlogListPage,
  Config,
  IngredientsPage,
  Page,
  ProductPage,
  SearchPage,
  SetCreatorPage,
  ShopPage,
} from 'cms-types'
import { Metadata, ResolvedMetadata, ResolvingMetadata } from 'next'
import { notFound } from 'next/navigation'
import QueryString from 'qs'
import { cache } from 'react'

import { getWebsiteUrl } from '@/helpers/getWebsiteUrl'

import { AnyBlock } from './blocks'
import {
  CMS_URL,
  errorWithCustomMessage,
  fetchCmsJson,
  getCmsOptionsAsSearchParams,
  Options,
} from './getCollection'
import { resolveRelation } from './helpers'

const REVALIDATE_TIME = 120

type PageTypes = {
  pages: Page
  shopPages: ShopPage
  ingredientsPages: IngredientsPage
  blogListPages: BlogListPage
  productPages: ProductPage
  searchPage: SearchPage
  setCreatorPage: SetCreatorPage
  accountPages: AccountPage
}

export const getPage = async <C extends keyof PageTypes>(
  collection: C,
  slug: string,
  {
    searchParams = {},
    throwOnNotFound = false,
  }: {
    searchParams?: Options
    throwOnNotFound?: boolean
  } = {},
): Promise<PageTypes[C]> => {
  const isPreview = searchParams.preview === '1'
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(searchParams)}${
    slug ? `&where[slug][equals]=${slug}` : ''
  }`

  try {
    const page = await fetchCmsJson<{ docs: PageTypes[C][]; totalDocs: number }>(
      url,
      {
        next: { revalidate: isPreview ? 0 : REVALIDATE_TIME },
      },
      { logLabel: 'CMS REQ PAGE' },
    )

    if (page.totalDocs === 0 || page.docs.length === 0) {
      if (throwOnNotFound) {
        throw new Error(`Page ${slug} not found`)
      }
    }

    return page.docs[0]
  } catch (error) {
    throw errorWithCustomMessage(error, 'collection', url)
  }
}

export const getGlobal = async <T extends keyof Config['globals']>(
  global: T,
  {
    searchParams = {},
  }: {
    searchParams?: Options
  } = {},
): Promise<Config['globals'][T]> => {
  const isPreview = searchParams.preview === '1'
  const url = `${CMS_URL}/api/globals/${global}?${getCmsOptionsAsSearchParams(searchParams)}`

  try {
    return await fetchCmsJson<Config['globals'][T]>(
      url,
      {
        next: { revalidate: isPreview ? 0 : REVALIDATE_TIME },
      },
      { logLabel: 'CMS REQ GLOBAL' },
    )
  } catch (error) {
    throw errorWithCustomMessage(error, 'global', url)
  }
}

export const getGlobalTypography = cache((locale: string) =>
  getGlobal('globalTypography', { searchParams: { locale } }),
)

export const getCollection = async <T extends keyof Config['collections']>(
  collection: T,
  {
    searchParams = {},
  }: {
    searchParams?: Options
  } = {},
): Promise<Config['collections'][T][]> => {
  const isPreview = searchParams.preview === '1'
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(searchParams)}`

  try {
    const { docs } = await fetchCmsJson<{ docs: Config['collections'][T][] }>(
      url,
      {
        next: { revalidate: isPreview ? 0 : REVALIDATE_TIME },
      },
      { logLabel: 'CMS REQ COLLECTION' },
    )

    return docs
  } catch (error) {
    throw errorWithCustomMessage(error, 'collection', url)
  }
}

/** Fetches the documents of a collection matching a Payload `where` query */
export const findInCollection = async <T extends keyof Config['collections']>(
  collection: T,
  where: Record<string, unknown>,
  {
    searchParams = {},
  }: {
    searchParams?: Options
  } = {},
): Promise<Config['collections'][T][]> => {
  const isPreview = searchParams.preview === '1'
  const query = QueryString.stringify({ where }, { encodeValuesOnly: true })
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(searchParams)}&${query}`

  try {
    const { docs } = await fetchCmsJson<{ docs: Config['collections'][T][] }>(
      url,
      {
        next: { revalidate: isPreview ? 0 : REVALIDATE_TIME },
      },
      { logLabel: 'CMS REQ COLLECTION' },
    )

    return docs
  } catch (error) {
    throw errorWithCustomMessage(error, 'collection', url)
  }
}

/** Product pages of all products that have the ingredient in at least one variant */
export const getProductPagesWithIngredient = async (ingredientId: string, locale: string) => {
  const products = await findInCollection(
    'products',
    { 'variants.ingredients.ingredient': { equals: ingredientId } },
    { searchParams: { locale, depth: '0', limit: '100' } },
  )

  if (products.length === 0) return []

  return findInCollection(
    'productPages',
    { product: { in: products.map(({ id }) => id) } },
    { searchParams: { locale, depth: '1', limit: '100' } },
  )
}

export const getCollectionItem = async <T extends keyof Config['collections']>(
  id: string,
  collection: T,
  {
    searchParams = {},
  }: {
    searchParams?: Options
  } = {},
): Promise<Config['collections'][T]> => {
  const isPreview = searchParams.preview === '1'
  const url = `${CMS_URL}/api/${collection}/${id}?${getCmsOptionsAsSearchParams(searchParams)}`

  try {
    return await fetchCmsJson<Config['collections'][T]>(
      url,
      {
        next: { revalidate: isPreview ? 0 : REVALIDATE_TIME },
      },
      { logLabel: 'CMS REQ ITEM' },
    )
  } catch (error) {
    throw errorWithCustomMessage(error, 'item', url)
  }
}

export const getCollectionItemByUniqueField = async <C extends keyof Config['collections']>(
  collection: C,
  field: string,
  equals: string,
  {
    searchParams = {},
    throwOnNotFound = false,
  }: {
    searchParams?: Options
    throwOnNotFound?: boolean
  } = {},
): Promise<Config['collections'][C]> => {
  const query = QueryString.stringify(
    {
      where: { [field]: { equals } },
    },
    {
      encodeValuesOnly: true,
    },
  )
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(searchParams)}&${query}`

  try {
    const item = await fetchCmsJson<{ docs: Config['collections'][C][]; totalDocs: number }>(
      url,
      {
        next: { revalidate: 0 },
      },
      { logLabel: 'CMS REQ ITEM_BY_ID' },
    )

    if (item.totalDocs === 0) {
      if (throwOnNotFound) {
        throw new Error(`Item with ${field}=${equals} not found`)
      } else {
        notFound()
      }
    }

    return item.docs[0]
  } catch (error) {
    throw errorWithCustomMessage(error, 'item', url)
  }
}

export const getPaginatedCollection = async <T extends keyof Config['collections']>(
  collection: T,
  {
    where = '',
    equals = '',
    page = 1, // Add page parameter with a default value of 1
    pageLimit = 10, // Add pageSize parameter with a default value of 10
    sortBy = '',
  }: {
    where?: string
    equals?: string
    page?: number
    pageLimit?: number
    sortBy?: string
  } = {},
  {
    searchParams = {},
  }: {
    searchParams?: Options
  } = {},
): Promise<{ items: Config['collections'][T][]; totalPages: number }> => {
  const isPreview = searchParams.preview === '1'
  const query = QueryString.stringify(
    {
      page,
      limit: pageLimit,
      sort: sortBy,
      where: where && equals ? { [where]: { equals } } : undefined,
    },
    {
      encodeValuesOnly: true,
    },
  )
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(searchParams)}&${query}`

  const responseJson = await fetchCmsJson<{
    docs: Config['collections'][T][]
    totalPages: number
  }>(
    url,
    {
      next: { revalidate: isPreview ? 0 : REVALIDATE_TIME },
    },
    { logLabel: 'CMS REQ PAGINATED' },
  )

  return {
    items: responseJson.docs,
    totalPages: responseJson.totalPages, // Extract total pages from the response
  }
}

export const getMetadata = async <T extends keyof Config['collections']>(
  collection: T,
  slug: string,
  path: string,
  {
    searchParams = {},
  }: {
    searchParams?: Options
  } = {},
  parent?: ResolvingMetadata,
  /** Title/description used when the document has no SEO meta, before falling back to the parent */
  getFallback?: (item: Config['collections'][T]) => { title?: string; description?: string },
): Promise<Metadata> => {
  try {
    const parentMetadata = (await parent) ?? ({} as ResolvedMetadata)
    const item = await getCollectionItemByUniqueField(collection, 'slug', slug, { searchParams })
    const meta = ('meta' in item ? item.meta : undefined) as unknown as Page['meta'] | undefined
    const fallback = getFallback?.(item) ?? {}
    const urlSearchParams = new URLSearchParams()
    const isDevOrStage =
      /^(?:http|https):\/\/dev\./.test(process.env.WEBSITE_PUBLIC_URL) ||
      /^(?:http|https):\/\/stage\./.test(process.env.WEBSITE_PUBLIC_URL)

    for (const [key, value] of Object.entries(searchParams)) {
      // 'variant' selects which product variant is shown, not a distinct page - keeping
      // it out of the canonical avoids fragmenting indexing/link equity per variant.
      if (key === 'locale' || key === 'variant') continue
      if (Array.isArray(value)) {
        value.forEach(v => urlSearchParams.append(key, v))
      } else {
        urlSearchParams.append(key, value)
      }
    }

    return {
      robots: {
        index: isDevOrStage ? false : !meta?.index ? false : true,
        follow: isDevOrStage ? false : !meta?.follow ? false : true,
      },
      title: meta?.title || fallback.title || parentMetadata.title,
      description: meta?.description || fallback.description || parentMetadata.description,
      openGraph: {
        images: resolveRelation(meta?.image)?.url ?? parentMetadata.openGraph?.images,
      },
      metadataBase: getWebsiteUrl(
        typeof searchParams.locale === 'string' ? searchParams.locale : undefined,
      ),

      alternates: {
        canonical: path
          ? `${path}${urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : ''}`
          : undefined,
      },
    }
  } catch (error) {
    console.error('Failed to resolve metadata', error)

    return {}
  }
}

type SearchInCollectionOptions = {
  /**
   * The key to search within the collection. For nested keys, use `.` notation:
   * ```javascript
   *  'object.key.nestedKey.anotherNestedKey'
   * ```
   *
   * Also support multiple key search within the collection:
   * ```javascript
   *  ['key1', 'key2.nestedKey', 'key3']
   * ```
   */
  key: string | string[]
  query: string
  sort?: string
  page?: number
}

export const searchInCollection = async <T extends keyof Config['collections']>(
  collection: T,
  { key, query, sort = 'id', page = 1 }: SearchInCollectionOptions,
  { searchParams = {} }: { searchParams?: Options } = {},
) => {
  const queryString = QueryString.stringify(
    {
      sort,
      page,
      where: {
        ...(Array.isArray(key)
          ? {
              or: key.map(k => ({ [k]: { contains: query } })),
            }
          : { [key]: { contains: query } }),
        'searchConfig.searchable': { equals: true },
      },
    },
    {
      encodeValuesOnly: true,
    },
  )
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(
    searchParams,
  )}&${queryString}`

  return await fetchCmsJson<{
    docs: Config['collections'][T][]
    totalDocs: number
    hasNextPage: boolean
  }>(url, undefined, { logLabel: 'CMS REQ SEARCH' })
}

export const getItemCountInCategories = async (
  collection: 'products' | 'blogPosts',
): Promise<Record<string, number>> => {
  const url = `${CMS_URL}/api/${
    collection === 'blogPosts' ? '/blogPostsCountPerCategory' : '/productsCountPerCategory'
  }`

  return await fetchCmsJson<Record<string, number>>(
    url,
    { next: { revalidate: REVALIDATE_TIME } },
    { logLabel: 'CMS REQ COUNT' },
  )
}

export const getBlocksForPage = async (
  slug: string,
  collection: 'pages' | 'accountPages' = 'pages',
): Promise<AnyBlock['blockType'][]> => {
  const url = `${CMS_URL}/api/getBlocksForPage/${collection}/${slug}`

  return await fetchCmsJson<AnyBlock['blockType'][]>(
    url,
    { next: { revalidate: REVALIDATE_TIME } },
    { logLabel: 'CMS REQ BLOCKS' },
  )
}

export const getBlocksForSetCreatorPage = async (
  blockPosition: 'previous' | 'after',
): Promise<AnyBlock['blockType'][]> => {
  const url = `${CMS_URL}/api/getBlocksForSetCreatorPage/${blockPosition}`

  return await fetchCmsJson<AnyBlock['blockType'][]>(
    url,
    { next: { revalidate: REVALIDATE_TIME } },
    { logLabel: 'CMS REQ SET_CREATOR_BLOCKS' },
  )
}

/**
 * The CMS is fetched with `defaultDepth: 1`, so relations more than one hop away
 * from the queried document arrive as unpopulated string ids. The helpers below
 * fetch any relation that is still a string, so the component works regardless of
 * how deeply the incoming data happens to be populated.
 */

export async function fetchIfId<T extends { id: string }>(
  relation: string | T | null | undefined,
  collection: keyof Config['collections'],
  locale: string,
): Promise<T | null> {
  if (relation == null) return null
  if (typeof relation !== 'string') return relation
  return (await getCollectionItem(relation, collection, {
    searchParams: { locale },
  })) as unknown as T
}
